import * as React from "react";
import { useEffect, useState } from "react";
import { TimePicker } from "@fluentui/react-timepicker-compat";
import { FluentField } from "./Fluent-Field";
import { getTimeFromTimeStamp } from "../../util/time-helpers";
import PropTypes from "prop-types";
import "./fluent-overide.scss";

// Coerce value into a Date, matching the legacy react-datepicker behavior
// which accepted Date instances, ISO strings, or Unix-ms numbers and silently
// converted them. Returns null when the input cannot be parsed.
const toDate = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (value instanceof Date) return isNaN(value) ? null : value;
  const parsed = new Date(value);
  return isNaN(parsed) ? null : parsed;
};

const formatTime = (date) => (date ? getTimeFromTimeStamp(date) : "");

export const FluentTime = ({
  label,
  value,
  onChange,
  disabled,
  id,
  name,
  className,
  timeIntervals = 15,
  required,
  validationMessage,
  ...rest
}) => {
  const dataTestId = rest["data-testid"];
  const selectedTime = toDate(value);

  // Fluent's TimePicker is a Combobox under the hood and requires BOTH
  // `selectedTime` (Date) AND `value` (formatted string) for controlled mode.
  // Without the input string, the field renders empty even when selectedTime
  // is set. We also keep a local copy so the user can type freely while we
  // wait for onTimeChange to commit a parsed Date back to the parent.
  const [inputValue, setInputValue] = useState(formatTime(selectedTime));

  // Sync the displayed input string when the parent value actually changes.
  // Use the time value as a stable primitive dep so a re-render with the same
  // moment doesn't reset typing mid-input.
  const valueKey = selectedTime ? selectedTime.getTime() : null;
  useEffect(() => {
    setInputValue(formatTime(selectedTime));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueKey]);

  const handleInput = (e) => {
    setInputValue(e.target.value);
  };

  const handleTimeChange = (_ev, data) => {
    if (!onChange) return;
    onChange(data?.selectedTime ?? null);
  };

  return (
    <FluentField
      label={label}
      validationMessage={validationMessage}
      required={required}
      componentStyle={className}
    >
      <TimePicker
        id={id}
        name={name}
        selectedTime={selectedTime}
        value={inputValue}
        onInput={handleInput}
        onTimeChange={handleTimeChange}
        disabled={disabled}
        increment={timeIntervals}
        hourCycle="h12"
        className={`fluentInput ${className || ""}`.trim()}
        data-testid={dataTestId}
      />
    </FluentField>
  );
};

FluentTime.propTypes = {
  label: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.instanceOf(Date),
    PropTypes.number,
    PropTypes.string,
  ]),
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  id: PropTypes.string,
  name: PropTypes.string,
  className: PropTypes.string,
  timeIntervals: PropTypes.number,
  required: PropTypes.bool,
  validationMessage: PropTypes.string,
};
